/**
 * `opening-off-wall` — a wall edit that would leave a door, window or opening
 * hanging past the end of its wall (#2860).
 *
 * A length edit (and the wall drag, which runs through the same
 * `computeStretch`) moves the opening by the box at its centre and the wall by
 * its junctions, so the two can part: shorten a wall past a door that stays,
 * or carry a door with the moving end past the end that stays. Nothing stopped
 * either, and the plan saved with the door in mid-air.
 *
 * The rule compares each opening with ITSELF before the edit, on the
 * structure-level copy (the canonical one; room copies are slim) against its
 * structure wall: the edit is blocked when it would put the opening more than
 * `OPENING_OVERHANG_TOL` further past its wall's end than it already was. So a
 * scanned door that already sits a little proud never blocks an edit that
 * leaves it so, and a cut that lands a few centimetres into a jamb is not a
 * door left in mid-air. Wall-parented OBJECTS are not checked: a shelf or a
 * cabinet may stand proud of a corner.
 *
 * See reference-docs/wall-drag/overview.md, "Validation".
 */
import {
  iterateAffectedStructureChildren,
  wallEndpointsXZ,
} from './wallDisplacement';

// How much further past its wall's end an edit may put an opening. A typed or
// dragged cut lands near a jamb, not on it.
export const OPENING_OVERHANG_TOL = 0.05; // 5 cm

const OPENING_ARRAYS = new Set(['doors', 'windows', 'openings']);

/**
 * How far an opening runs past either end of its wall, along it (≥ 0).
 *
 * The wall is read from its centre line (`wallEndpointsXZ`), the geometry the
 * plan itself moves — NOT from `alignment`, which `getWallAxisXZ` prefers: a
 * plan's `after` keeps the wall's old `alignment` corners, so a structure wall
 * that carries them would be measured where it was.
 */
export function openingOverhang(opening, wall) {
  const { a, dir, length } = wallEndpointsXZ(wall);
  if (!(length > 0) || !opening?.center) return 0;
  const along =
    (opening.center.x - a.x) * dir.x + (opening.center.z - a.z) * dir.z;
  const half = (opening.dimensions?.x || 0) / 2;
  return Math.max(0, half - along, along + half - length);
}

/**
 * The least an opening could run past its wall's ends wherever it sat on it:
 * half of whatever of its width the wall cannot hold, at each end (≥ 0).
 */
function leastOverhang(opening, wall) {
  const { length } = wallEndpointsXZ(wall);
  return Math.max(0, ((opening.dimensions?.x || 0) - length) / 2);
}

/**
 * @param {Object} structureData - the structure the plan was computed over.
 * @param {Object} plan
 * @param {Array} plan.affectedStructureWalls - `[{ wallId, before, after }]`.
 * @param {Array} plan.affectedItems - the cascade's moved children; an opening
 *   missing from it stays where it is.
 * @returns {Array} one blocking `opening-off-wall` error per opening. Its
 *   `ref.widerThanWall` says no position on the new wall would pass either,
 *   so moving the opening cannot help: it has to be narrowed.
 */
export function detectOpeningsOffWalls(
  structureData,
  { affectedStructureWalls, affectedItems }
) {
  const movedTo = new Map();
  for (const item of affectedItems || []) {
    // Structure-level copies only: room copies carry a room id, exterior
    // copies a scope, and both are the same openings again.
    if (item.roomId || item.scope) continue;
    movedTo.set(`${item.kind}:${item.id}`, item.after);
  }

  const errors = [];
  for (const entry of affectedStructureWalls || []) {
    for (const { arrayName, child } of iterateAffectedStructureChildren(
      structureData,
      entry.wallId
    )) {
      if (!OPENING_ARRAYS.has(arrayName) || !child.center) continue;
      const kind = arrayName.slice(0, -1);
      const after = movedTo.get(`${kind}:${child.id}`) || child;
      const was = openingOverhang(child, entry.before);
      const allowed = was + OPENING_OVERHANG_TOL;
      const overhang = openingOverhang(after, entry.after);
      if (overhang > allowed) {
        errors.push({
          code: 'opening-off-wall',
          ref: {
            wallId: entry.wallId,
            openingId: child.id,
            kind,
            overhang,
            widerThanWall: leastOverhang(after, entry.after) > allowed,
          },
        });
      }
    }
  }
  return errors;
}
